import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BirthdayFundCertificateDesigner, type CertificateDesignerData } from "./BirthdayFundCertificateDesigner";
import { useExportImage } from "./useExportImage";
import { 
  useGetCertificateTemplate, 
  useCreateCertificateInstance
} from "@/lib/api/birthdayFundCertificates";
import { GenerateLoadingOrError } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { generateUniqueShortenedPathname } from "@/lib/hash/index";
import { downloadBlob, UploadPresignedBlob } from "@/lib/file";
import { waitFor } from "@testing-library/react";
import { wrapPngInPdf } from "@/lib/pdf/pngToPdf";

export function BirthdayFundCreateInstance() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const templateId = searchParams.get("templateId");
  const { exportToBlob, isExporting, error } = useExportImage();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<Partial<CertificateDesignerData> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [instanceCertificateId, setInstanceCertificateId] = useState<undefined | string>(undefined);

  
  const templateRes = useGetCertificateTemplate(templateId);
  const createInstance = useCreateCertificateInstance();
  
  useEffect(() => {
    if (templateId && templateRes.data?.success) {
      const template = templateRes.data.data.template;
      setInitialData(template.template_data as Partial<CertificateDesignerData>);
    } else if (!templateId) {
      // No template ID, start with empty data
      setInitialData({});
    }
  }, [templateId, templateRes.data]);

  const loadingOrErrorUi = templateId ? GenerateLoadingOrError([templateRes]) : null;

  if (loadingOrErrorUi) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Create Certificate Instance
        </h1>
        {loadingOrErrorUi}
      </section>
    );
  }

  // Don't render designer until we have initial data
  if (initialData === null) {
    return null;
  }

  const handleCreateInstance = async (data: CertificateDesignerData, previewElement: HTMLDivElement) => {
    setSaveError(null);
    setIsCreating(true);
    
    try {
      // Create instance via API
      const instanceResult = await createInstance({
        certificate_id: `#-----`,
        location_name: data.locationName,
        template_id: templateId || undefined,
      });
      
      if (!instanceResult.success) {
        throw instanceResult;
      }

      const certificateId = instanceResult.data.instance.certificate_id;
      setInstanceCertificateId(certificateId);
      
      const presignedUrl = instanceResult.data.presigned_put_url;

      await new Promise(res => setTimeout(res, 300));
      
      // Generate PNG blob
      const pngBlob = await exportToBlob(previewElement, {
        scale: 2,
        backgroundColor: "#ffffff",
      });
      
      if (!pngBlob) {
        throw new Error("Failed to generate certificate image");
      }
      
      // Wrap PNG in PDF
      const pdfBlob = await wrapPngInPdf(pngBlob);
      
      // Upload to S3
      const uploadResult = await UploadPresignedBlob(presignedUrl, pdfBlob, "application/pdf");
      
      if (!uploadResult.success) {
        throw new Error("Failed to upload certificate to storage");
      }

      await downloadBlob(pdfBlob, `birthday-beacon-certificate-${certificateId}.pdf`);
      
      toast({
        title: "Instance Created",
        description: `Certificate #${certificateId} has been created successfully.`,
      });
      
      // Navigate to dashboard after successful creation
      navigate("/fund/birthday/certificates");
      
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to create instance";
      setSaveError(errorMessage);
      console.error("Create instance error:", err);
      
      toast({
        title: "Failed to Create Instance",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
        <div>
            {error && (
                <div className="mx-6 mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                    Export failed: {error.message}
                </div>
            )}
            {saveError && (
                <div className="mx-6 mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                    Failed to create instance: {saveError}
                </div>
            )}

            <BirthdayFundCertificateDesigner
                initialData={initialData}
                onCreateInstance={handleCreateInstance}
                isProcessing={isExporting || isCreating}
                instanceCertificateId={instanceCertificateId}
            />
        </div>
    );
}

export default BirthdayFundCreateInstance;
